import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Widget, getWidgetData } from '../types/widget';
import { DataVisualizer } from './DataVisualizer';
import { truncateWidgetTitle } from '../utils/textUtils';
import { getTenantId, getAccessToken } from '../utils/storage';
import Toast from './Toast';
import { exportWidget, exportQueryResponse, getExportLabel, isTableVisualization } from '../services/exportService';

interface WidgetCardProps {
    widget: Widget;
    onDelete: (widget: Widget) => void;
    onAskMore?: (contextQuery: string, contextData?: any) => void;
    dragHandleProps?: any;
    isDragging?: boolean;
    readOnly?: boolean;
    hideManagementActions?: boolean;
}

// Zoomed Widget Modal Component - Full Screen
const ZoomedWidgetModal: React.FC<{ widget: Widget; onClose: () => void }> = ({ widget, onClose }) => {
    const [isLoading, setIsLoading] = useState(true);

    // Simulate loading and then show content
    React.useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Dynamic sizing based on visualization type and data
    const getDynamicModalSize = () => {
        const vizType = widget.visualization_type?.toLowerCase() || 'table';
        const dataLength = getWidgetData(widget).length;

        // Chart types benefit from wider displays
        const isChart = ['bar', 'line', 'pie', 'donut', 'doughnut', 'area', 'scatter', 'gauge', 'map'].some(type =>
            vizType.includes(type) || vizType.includes('chart')
        );

        // Table types benefit from more height for rows
        const isTable = vizType.includes('table') || vizType === 'table';

        // Metric cards are compact
        const isMetric = vizType.includes('metric');

        if (isMetric) {
            return {
                width: 'w-full max-w-xs sm:max-w-sm md:max-w-2xl',
                height: 'max-h-[40vh] sm:max-h-[45vh] md:max-h-[50vh]',
                padding: 'p-4 sm:p-5 md:p-6'
            };
        } else if (isTable) {
            // Tables need more height for rows, moderate width
            const heightClass = dataLength > 20 ?
                'max-h-[90vh] sm:max-h-[92vh] md:max-h-[95vh]' :
                dataLength > 10 ?
                    'max-h-[80vh] sm:max-h-[82vh] md:max-h-[85vh]' :
                    'max-h-[65vh] sm:max-h-[68vh] md:max-h-[70vh]';
            return {
                width: 'w-full max-w-sm sm:max-w-2xl md:max-w-4xl lg:max-w-5xl',
                height: heightClass,
                padding: 'p-3 sm:p-4 md:p-4'
            };
        } else if (isChart) {
            // Charts need wide display for proper visualization
            return {
                width: 'w-full max-w-lg sm:max-w-3xl md:max-w-5xl lg:max-w-6xl xl:max-w-7xl',
                height: 'h-[75vh] sm:h-[80vh] md:h-[85vh]',
                padding: 'p-4 sm:p-6 md:p-8'
            };
        } else {
            // Default fallback
            return {
                width: 'w-full max-w-sm sm:max-w-2xl md:max-w-4xl lg:max-w-6xl',
                height: 'max-h-[70vh] sm:max-h-[75vh] md:max-h-[80vh]',
                padding: 'p-4 sm:p-5 md:p-6'
            };
        }
    };

    const modalSize = getDynamicModalSize();

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={handleBackdropClick}
        >
            <div
                className={`bg-white dark:bg-slate-800 rounded-3xl shadow-2xl ring-1 ring-black/5 dark:ring-white/10 w-full ${modalSize.width} ${modalSize.height} flex flex-col border border-slate-200/50 dark:border-slate-700/50 backdrop-blur-xl overflow-hidden`}
                onClick={(e) => e.stopPropagation()}
                style={{
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.05)'
                }}
            >
                {/* Modal Header */}
                <div className="flex justify-between items-center p-6 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-white to-slate-50/50 dark:from-slate-800 dark:to-slate-800/50">
                    <div className="flex-1 min-w-0 flex items-center space-x-4">
                        <div className="flex-1 min-w-0">
                            <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-white dark:to-slate-200 bg-clip-text text-transparent truncate" title={widget.title}>
                                {truncateWidgetTitle(widget.title)}
                            </h1>
                            <div className="mt-3 flex items-center space-x-4">
                                <div className="inline-flex items-center px-4 py-2 text-xs font-bold bg-gradient-to-r from-blue-500/15 to-purple-500/15 dark:from-blue-400/20 dark:to-purple-400/20 text-blue-700 dark:text-blue-300 rounded-full border border-blue-200/40 dark:border-blue-400/30 backdrop-blur-sm shadow-sm">
                                    <svg className="w-3 h-3 mr-2 text-blue-500 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    {widget.visualization_type?.toUpperCase() || 'VISUALIZATION'}
                                </div>
                                <div className="inline-flex items-center px-4 py-2 text-xs font-semibold bg-gradient-to-r from-emerald-500/15 to-teal-500/15 dark:from-emerald-400/20 dark:to-teal-400/20 text-emerald-700 dark:text-emerald-300 rounded-full border border-emerald-200/40 dark:border-emerald-400/30 backdrop-blur-sm shadow-sm">
                                    <svg className="w-3 h-3 mr-2 text-emerald-500 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    {getWidgetData(widget).length} {getWidgetData(widget).length === 1 ? 'record' : 'records'}
                                </div>
                            </div>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-3 rounded-xl hover:bg-gradient-to-r hover:from-red-50 hover:to-rose-50 dark:hover:from-red-900/20 dark:hover:to-rose-900/20 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-all duration-300 border border-transparent hover:border-red-200 dark:hover:border-red-800 transform hover:scale-105"
                        title="Close full screen view (ESC)"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Modal Content */}
                <div className="flex-1 overflow-auto bg-gradient-to-br from-slate-50/50 to-white dark:from-slate-900/50 dark:to-slate-800">
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full p-8">
                            <div className="text-center">
                                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-primary/20 border-t-primary mb-4"></div>
                                <p className="text-slate-600 dark:text-slate-400 font-medium">Loading widget in full screen...</p>
                                <p className="text-slate-500 dark:text-slate-500 text-sm mt-2">Preparing your enhanced visualization</p>
                            </div>
                        </div>
                    ) : (
                        <div className="h-full">
                            {getWidgetData(widget).length > 0 ? (
                                <div className={`h-full ${modalSize.padding} relative`}>
                                    {/* Enhanced background for charts */}
                                    <div className="absolute inset-0 bg-white/60 dark:bg-slate-800/60 rounded-xl border border-slate-200/30 dark:border-slate-700/30 backdrop-blur-sm"></div>
                                    <div className="relative z-10 h-full flex flex-col">
                                        <DataVisualizer
                                            data={getWidgetData(widget)}
                                            visualizationType={widget.visualization_type || 'table'}
                                            title={widget.visualization_type?.toLowerCase() === 'gauge' ? widget.title : undefined}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center h-full text-slate-400 relative">
                                    <div className="absolute inset-0 bg-gradient-to-br from-slate-50/80 to-slate-100/80 dark:from-slate-800/80 dark:to-slate-900/80 rounded-xl"></div>
                                    <div className={`text-center ${modalSize.padding} relative z-10`}>
                                        <div className="bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-700 dark:to-slate-800 rounded-2xl p-6 mb-6 mx-auto w-fit shadow-lg border border-slate-200/50 dark:border-slate-600/50">
                                            <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-bold mb-3 text-slate-700 dark:text-slate-300">No Data Available</h3>
                                        <p className="text-slate-500 dark:text-slate-400 text-base">This visualization doesn't have any data to display</p>
                                        <div className="mt-4 text-sm text-slate-400 dark:text-slate-500">
                                            Try refreshing the widget or check your data source
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export const WidgetCard: React.FC<WidgetCardProps> = ({ widget, onDelete, onAskMore, dragHandleProps, isDragging, readOnly = false, hideManagementActions = false }) => {
    // Clean, uniform sizing for metric/chart widgets; tables expand to fit content
    const isTableWidget = (() => {
        const type = widget.visualization_type?.toLowerCase() || '';
        return type.includes('table') || type.includes('list') || type.includes('log') ||
            type.includes('activity') || type === 'data' || type === 'grid' ||
            (!type.includes('chart') && !type.includes('bar') && !type.includes('line') &&
             !type.includes('pie') && !type.includes('donut') && !type.includes('doughnut') &&
             !type.includes('area') && !type.includes('gauge') &&
             !type.includes('graph') && !type.includes('plot') && !type.includes('map') &&
             !type.includes('metric') && !type.includes('number') && !type.includes('summary') &&
             type !== 'kpi' && type !== 'stat' && type !== 'metric_card' && type !== 'metric');
    })();
    const isMapWidget = widget.visualization_type?.toLowerCase() === 'map';
    const cardHeight = isTableWidget ? 'auto' : isMapWidget ? '460px' : '320px';
    const cardMinHeight = isTableWidget ? '200px' : undefined;
    const canUseWidgetActions = !readOnly;
    const canManageWidget = !readOnly && !hideManagementActions;
    const [isZoomed, setIsZoomed] = useState(false);
    const [showKebabMenu, setShowKebabMenu] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);
    const [isExporting, setIsExporting] = useState(false);

    // Check if this is a metric card to render differently
    const isMetricCard = widget.visualization_type === 'metric_card' || widget.visualization_type === 'metric';



    const handleZoom = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setIsZoomed(true);
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
    };

    const handleCloseZoom = () => {
        setIsZoomed(false);
        // Restore body scroll
        document.body.style.overflow = '';
    };

    const handleInfo = (e?: React.MouseEvent) => {
        console.log('handleInfo called!');
        e?.stopPropagation();
        setShowKebabMenu(false);
        setShowInfoModal(true);
        console.log('showInfoModal set to:', true);
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
    };

    const handleCloseInfoModal = () => {
        setShowInfoModal(false);
        // Restore body scroll
        document.body.style.overflow = '';
    };

    const handleEdit = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setShowKebabMenu(false);
        setEditTitle(widget.title);
        setShowEditModal(true);
        document.body.style.overflow = 'hidden';
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditTitle('');
        setIsUpdating(false);
        setShowSuccess(false);
        document.body.style.overflow = '';
    };

    const handleUpdateTitle = async () => {
        if (!editTitle.trim() || editTitle === widget.title) {
            handleCloseEditModal();
            return;
        }

        setIsUpdating(true);
        try {
            const tenantId = getTenantId();
            const accessToken = getAccessToken();
            let url = `${process.env.REACT_APP_API_URL}/v1/widgets/${widget.id}`;
            if (tenantId) {
                url += `?tenant_id=${tenantId}`;
            }

            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(accessToken && { 'Authorization': `Bearer ${accessToken}` })
                },
                body: JSON.stringify({
                    title: editTitle.trim()
                })
            });

            if (response.ok) {
                // Update the widget title locally
                widget.title = editTitle.trim();
                setShowSuccess(true);
                setToast({ message: 'Widget title updated successfully!', type: 'success' });
                setTimeout(() => {
                    handleCloseEditModal();
                }, 1500);
            } else {
                console.error('Failed to update widget title');
                setToast({ message: 'Failed to update widget title. Please try again.', type: 'error' });
            }
        } catch (error) {
            console.error('Error updating widget title:', error);
            setToast({ message: 'Error updating widget title. Please try again.', type: 'error' });
        } finally {
            setIsUpdating(false);
        }
    };

    const handleDelete = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setShowKebabMenu(false);
        // Directly call parent's onDelete which will show its own confirmation modal
        onDelete(widget);
    };

    const handleExport = async (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setShowKebabMenu(false);

        if (isExporting) return;

        setIsExporting(true);
        try {
            // For non-table visualizations, try to capture the chart image
            let chartImage: string | undefined;
            const vizType = widget.visualization_type || 'table';

            if (!isTableVisualization(vizType)) {
                // Try to find and capture the chart canvas
                const widgetElement = document.querySelector(`[data-widget-id="${widget.id}"]`);
                const canvas = widgetElement?.querySelector('canvas') as HTMLCanvasElement;

                if (canvas) {
                    try {
                        chartImage = canvas.toDataURL('image/png').split(',')[1]; // Get base64 without prefix
                        console.log('Captured chart image for export');
                    } catch (canvasError) {
                        console.warn('Could not capture chart canvas:', canvasError);
                    }
                }
            }

            await exportWidget(
                widget.id.toString(),
                vizType,
                chartImage
            );
            setToast({ message: 'Export completed successfully!', type: 'success' });
        } catch (error) {
            console.error('Export failed:', error);
            setToast({ message: `Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`, type: 'error' });
        } finally {
            setIsExporting(false);
        }
    };

    // Handle ESC key to close modals
    React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (isZoomed) {
                    handleCloseZoom();
                } else if (showInfoModal) {
                    handleCloseInfoModal();
                } else if (showEditModal) {
                    handleCloseEditModal();
                }
            }
        };

        if (isZoomed || showInfoModal || showEditModal) {
            document.addEventListener('keydown', handleKeyDown);
            return () => {
                document.removeEventListener('keydown', handleKeyDown);
            };
        }
    }, [isZoomed, showInfoModal, showEditModal]);

    // Cleanup scroll when component unmounts
    React.useEffect(() => {
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    // Handle clicking outside kebab menu to close it
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Element;
            if (showKebabMenu && !target.closest('.kebab-menu-container') && !target.closest('.kebab-menu-container-dv')) {
                setShowKebabMenu(false);
            }
        };

        if (showKebabMenu) {
            document.addEventListener('click', handleClickOutside, true);
            return () => {
                document.removeEventListener('click', handleClickOutside, true);
            };
        }
    }, [showKebabMenu]);

    // Check if this is a chart to render without card styling
    const isChart = widget.visualization_type?.toLowerCase().includes('chart') ||
        widget.visualization_type === 'bar' ||
        widget.visualization_type === 'line' ||
        widget.visualization_type === 'pie' ||
        widget.visualization_type === 'donut' ||
        widget.visualization_type === 'area' ||
        widget.visualization_type === 'map' ||
        widget.visualization_type?.toLowerCase().includes('graph') ||
        widget.visualization_type?.toLowerCase().includes('plot');

    // Special rendering for metric cards without wrapper styling
    if (isMetricCard) {
        return (
            <>
                {getWidgetData(widget).length > 0 ? (
                    <div className="relative" data-widget-id={widget.id}>
                        <DataVisualizer
                            data={getWidgetData(widget)}
                            visualizationType={widget.visualization_type || 'metric'}
                            title={widget.title}
                            onAskMore={readOnly ? undefined : onAskMore}
                            onInfo={canManageWidget ? handleInfo : undefined}
                            onEdit={canManageWidget ? handleEdit : undefined}
                            onDelete={canManageWidget ? handleDelete : undefined}
                            showKebabMenu={readOnly ? false : showKebabMenu}
                            setShowKebabMenu={readOnly ? undefined : setShowKebabMenu}
                            dragHandleProps={canManageWidget ? dragHandleProps : undefined}
                            widgetId={widget.id.toString()}
                        />
                    </div>
                ) : (
                    <div className="group bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex items-center space-x-4 relative hover:shadow-md transition-shadow h-full min-h-[120px] metric-card-container">
                        {/* Drag Handle - required for @hello-pangea/dnd */}
                        {canManageWidget && (
                            <div
                                {...dragHandleProps}
                                className="drag-handle cursor-move rounded-lg bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex-shrink-0 transition-all duration-200"
                                title="Drag to reorder"
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, padding: 0 }}
                            >
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                </svg>
                            </div>
                        )}
                        <div className="p-3 rounded-full bg-slate-100 text-slate-400">
                            <span className="material-symbols-outlined">error</span>
                        </div>
                        <div className="flex-1">
                            <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">N/A</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 font-medium uppercase">NO DATA</div>
                        </div>
                    </div>
                )}

                {/* Info Modal for metric cards */}
                {showInfoModal && ReactDOM.createPortal(
                    <div
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
                        onClick={(e) => e.target === e.currentTarget && handleCloseInfoModal()}
                    >
                        <div
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center space-x-3">
                                        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-xl">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">Widget Information</h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400">Details and metadata</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCloseInfoModal}
                                        className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-all duration-200"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="space-y-4">
                                    {/* Title */}
                                    <div className="bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-800 dark:to-blue-900/20 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Title</label>
                                        <p className="text-sm font-medium text-slate-800 dark:text-slate-100 break-words">{widget.title}</p>
                                    </div>

                                    {/* Query */}
                                    <div className="bg-gradient-to-r from-slate-50 to-green-50 dark:from-slate-800 dark:to-green-900/20 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Query</label>
                                        <p className="text-sm text-slate-700 dark:text-slate-300 break-words leading-relaxed">{widget.users_query || 'No query available'}</p>
                                    </div>

                                    {/* Details Grid */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
                                            <label className="block text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-1">Visualization</label>
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100 capitalize">{widget.visualization_type || 'Table'}</p>
                                        </div>

                                        <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
                                            <label className="block text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider mb-1">Records</label>
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{getWidgetData(widget).length} items</p>
                                        </div>
                                    </div>

                                    {/* Timestamps */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800">
                                            <label className="block text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1">Created</label>
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{new Date(widget.created_at).toLocaleDateString()}</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(widget.created_at).toLocaleTimeString()}</p>
                                        </div>

                                        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 p-3 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                            <label className="block text-xs font-semibold text-yellow-600 dark:text-yellow-400 uppercase tracking-wider mb-1">Updated</label>
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{new Date(widget.updated_at).toLocaleDateString()}</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(widget.updated_at).toLocaleTimeString()}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="flex justify-end mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                                    <button
                                        onClick={handleCloseInfoModal}
                                        className="px-6 py-2.5 text-sm font-medium bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                {/* Edit Modal for metric cards */}
                {showEditModal && ReactDOM.createPortal(
                    <div
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
                        onClick={(e) => e.target === e.currentTarget && handleCloseEditModal()}
                    >
                        <div
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center space-x-3">
                                        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-xl">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">Edit Widget Title</h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400">Update the widget title</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCloseEditModal}
                                        className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-all duration-200"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="mb-6">
                                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Widget Title</label>
                                        <input
                                            type="text"
                                            value={editTitle}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            placeholder="Enter widget title"
                                            disabled={isUpdating}
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleUpdateTitle();
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="flex justify-end space-x-3">
                                    <button
                                        onClick={handleCloseEditModal}
                                        className="px-6 py-2.5 text-sm font-medium bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-300"
                                        disabled={isUpdating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleUpdateTitle}
                                        className="px-6 py-2.5 text-sm font-medium bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                        disabled={isUpdating || !editTitle.trim()}
                                    >
                                        {isUpdating ? 'Updating...' : 'Update Title'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                {/* Zoom Modal for metric cards */}
                {isZoomed && (
                    <ZoomedWidgetModal widget={widget} onClose={handleCloseZoom} />
                )}
            </>
        );
    }

    // Special rendering for charts with background but no borders or card effects
    if (isChart) {
        return (
            <>
                {getWidgetData(widget).length > 0 ? (
                    <div
                        data-widget-id={widget.id}
                        className={`bg-gradient-to-br from-white to-slate-50 dark:from-gray-800 dark:to-gray-900 rounded-xl transition-all duration-300 w-full h-full flex flex-col group ${isDragging
                            ? 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 scale-105'
                            : ''
                            }`}>
                        {/* Header section with title and buttons */}
                        <div className="flex justify-between items-start p-4 pb-3 bg-gradient-to-r from-transparent to-slate-50/30 dark:to-gray-800/30 rounded-t-xl flex-shrink-0">
                            <div className="flex items-center flex-1 min-w-0 mr-2">
                                {/* Drag Handle - hidden in readOnly/report mode */}
                                {canManageWidget && (
                                    <div
                                        {...dragHandleProps}
                                        className="drag-handle cursor-move rounded-lg bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex-shrink-0 transition-all duration-200 mr-2"
                                        title="Drag to reorder"
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0 }}
                                    >
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                        </svg>
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <h3
                                        className={`text-sm font-bold text-slate-800 dark:text-white group-hover:text-primary transition-colors duration-300 ${
                                            hideManagementActions ? 'whitespace-normal break-words leading-snug' : 'truncate'
                                        }`}
                                        title={widget.title}
                                    >
                                        {hideManagementActions ? widget.title : truncateWidgetTitle(widget.title)}
                                    </h3>
                                </div>
                            </div>
                            {canUseWidgetActions && (
                                <div className="flex space-x-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    {onAskMore && (
                                        <button
                                            onClick={(e) => { e.stopPropagation(); onAskMore(widget.users_query || widget.title, widget); }}
                                            className="p-1.5 rounded-md hover:bg-gradient-to-r hover:from-emerald-500 hover:to-green-500 text-slate-400 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                                            title="Ask more about this data"
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>auto_awesome</span>
                                        </button>
                                    )}
                                    <button
                                        onClick={handleZoom}
                                        className="p-1.5 rounded-md hover:bg-gradient-to-r hover:from-blue-500 hover:to-primary text-slate-400 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                                        title="Expand to full screen"
                                    >
                                        <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>open_in_full</span>
                                    </button>
                                    {isTableWidget && (
                                        <button
                                            disabled={isExporting}
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                if (isExporting) return;
                                                setIsExporting(true);
                                                try {
                                                    await exportQueryResponse(getWidgetData(widget), widget.title, 'table');
                                                } catch (err) {
                                                    setToast({ message: `Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`, type: 'error' });
                                                } finally {
                                                    setIsExporting(false);
                                                }
                                            }}
                                            className="p-1.5 rounded-md hover:bg-gradient-to-r hover:from-green-500 hover:to-emerald-500 text-slate-400 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md disabled:opacity-50"
                                            title="Export"
                                        >
                                            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                            </svg>
                                        </button>
                                    )}
                                    {/* Kebab Menu */}
                                    {canManageWidget && (
                                    <div className="relative z-10 kebab-menu-container">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setShowKebabMenu(!showKebabMenu); }}
                                            className="p-1.5 rounded-md hover:bg-gradient-to-r hover:from-slate-500 hover:to-slate-600 text-slate-400 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                                            title="More options"
                                        >
                                            <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                            </svg>
                                        </button>

                                        {showKebabMenu && (
                                            <div className="absolute right-0 top-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 min-w-[120px]">
                                                <button
                                                    onClick={handleInfo}
                                                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2 rounded-t-lg"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                    <span>Info</span>
                                                </button>
                                                <button
                                                    onClick={handleEdit}
                                                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                    </svg>
                                                    <span>Edit</span>
                                                </button>
                                                <button
                                                    onClick={handleExport}
                                                    disabled={isExporting}
                                                    className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2 disabled:opacity-50"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                    </svg>
                                                    <span>{isExporting ? 'Exporting...' : getExportLabel(widget.visualization_type || 'bar')}</span>
                                                </button>
                                                <button
                                                    onClick={handleDelete}
                                                    className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2 rounded-b-lg"
                                                >
                                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                    <span>Delete</span>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Chart content area - uses remaining space */}
                        <div className="flex-1 p-4 pt-0 min-h-0">
                            <DataVisualizer
                                data={getWidgetData(widget)}
                                visualizationType={widget.visualization_type || 'bar'}
                                title={widget.visualization_type?.toLowerCase() === 'gauge' ? widget.title : undefined}
                                onAskMore={onAskMore}
                                widgetId={widget.id.toString()}
                            />
                        </div>
                    </div>
                ) : (
                    <div className="bg-gradient-to-br from-white to-slate-50 dark:from-gray-800 dark:to-gray-900 rounded-xl w-full h-full flex flex-col text-slate-400">
                        {/* Header with drag handle - required for @hello-pangea/dnd */}
                        <div className="flex items-center p-4 pb-2">
                            {canManageWidget && (
                                <div
                                    {...dragHandleProps}
                                    className="drag-handle cursor-move rounded-lg bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex-shrink-0 transition-all duration-200 mr-2"
                                    title="Drag to reorder"
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0 }}
                                >
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                    </svg>
                                </div>
                            )}
                            <h3
                                className={`text-sm font-bold text-slate-800 dark:text-white ${
                                    hideManagementActions ? 'whitespace-normal break-words leading-snug' : 'truncate'
                                }`}
                                title={widget.title}
                            >
                                {hideManagementActions ? widget.title : truncateWidgetTitle(widget.title)}
                            </h3>
                        </div>
                        {/* Empty state content */}
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center">
                                <span className="material-symbols-outlined text-4xl mb-2 block opacity-50">
                                    trending_up
                                </span>
                                <p className="text-sm">No chart data</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Info Modal for charts */}
                {showInfoModal && ReactDOM.createPortal(
                    <div
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
                        onClick={(e) => e.target === e.currentTarget && handleCloseInfoModal()}
                    >
                        <div
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center space-x-3">
                                        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-xl">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">Widget Information</h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400">Details and metadata</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCloseInfoModal}
                                        className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-all duration-200"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="space-y-4">
                                    {/* Title */}
                                    <div className="bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-800 dark:to-blue-900/20 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Title</label>
                                        <p className="text-sm font-medium text-slate-800 dark:text-slate-100 break-words">{widget.title}</p>
                                    </div>

                                    {/* Query */}
                                    <div className="bg-gradient-to-r from-slate-50 to-green-50 dark:from-slate-800 dark:to-green-900/20 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                        <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Query</label>
                                        <p className="text-sm text-slate-700 dark:text-slate-300 break-words leading-relaxed">{widget.users_query || 'No query available'}</p>
                                    </div>

                                    {/* Details Grid */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
                                            <label className="block text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-1">Visualization</label>
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100 capitalize">{widget.visualization_type || 'Table'}</p>
                                        </div>

                                        <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
                                            <label className="block text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider mb-1">Records</label>
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{getWidgetData(widget).length} items</p>
                                        </div>
                                    </div>

                                    {/* Timestamps */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800">
                                            <label className="block text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1">Created</label>
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{new Date(widget.created_at).toLocaleDateString()}</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(widget.created_at).toLocaleTimeString()}</p>
                                        </div>

                                        <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 p-3 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                            <label className="block text-xs font-semibold text-yellow-600 dark:text-yellow-400 uppercase tracking-wider mb-1">Updated</label>
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{new Date(widget.updated_at).toLocaleDateString()}</p>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(widget.updated_at).toLocaleTimeString()}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="flex justify-end mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                                    <button
                                        onClick={handleCloseInfoModal}
                                        className="px-6 py-2.5 text-sm font-medium bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                {/* Edit Modal for charts */}
                {showEditModal && ReactDOM.createPortal(
                    <div
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
                        onClick={(e) => e.target === e.currentTarget && handleCloseEditModal()}
                    >
                        <div
                            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="p-6">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center space-x-3">
                                        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-xl">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">Edit Widget Title</h3>
                                            <p className="text-sm text-slate-500 dark:text-slate-400">Update the widget title</p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCloseEditModal}
                                        className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-all duration-200"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="mb-6">
                                    <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
                                        <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Widget Title</label>
                                        <input
                                            type="text"
                                            value={editTitle}
                                            onChange={(e) => setEditTitle(e.target.value)}
                                            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                            placeholder="Enter widget title"
                                            disabled={isUpdating}
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleUpdateTitle();
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="flex justify-end space-x-3">
                                    <button
                                        onClick={handleCloseEditModal}
                                        className="px-6 py-2.5 text-sm font-medium bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-300"
                                        disabled={isUpdating}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleUpdateTitle}
                                        className="px-6 py-2.5 text-sm font-medium bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                        disabled={isUpdating || !editTitle.trim()}
                                    >
                                        {isUpdating ? 'Updating...' : 'Update Title'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                {/* Zoom Modal for charts */}
                {isZoomed && (
                    <ZoomedWidgetModal widget={widget} onClose={handleCloseZoom} />
                )}
            </>
        );
    }

    return (
        <>
            <div
                data-widget-id={widget.id}
                className={`bg-gradient-to-br from-white to-slate-50 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-slate-200/50 dark:border-gray-700/50 flex flex-col group backdrop-blur-sm ${isDragging
                    ? 'shadow-2xl ring-2 ring-primary/50 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 scale-105'
                    : ''
                    }`}
                style={{ height: cardHeight, minHeight: cardMinHeight }}
            >
                <div className="flex justify-between items-start p-4 pb-3 bg-gradient-to-r from-transparent to-slate-50/30 dark:to-gray-800/30 rounded-t-xl">
                    <div className="flex items-center flex-1 min-w-0 mr-2">
                        {/* Drag Handle - hidden in readOnly/report mode */}
                        {canManageWidget && (
                            <div
                                {...dragHandleProps}
                                className="drag-handle cursor-move rounded-lg bg-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex-shrink-0 transition-all duration-200 mr-2"
                                title="Drag to reorder"
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, padding: 0 }}
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                </svg>
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <h3 className="text-sm font-bold text-slate-800 dark:text-white truncate group-hover:text-primary transition-colors duration-300" title={widget.title}>
                                {truncateWidgetTitle(widget.title)}
                            </h3>
                        </div>
                    </div>
                    {canUseWidgetActions && (
                        <div className="flex space-x-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            {onAskMore && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onAskMore(widget.users_query || widget.title, widget); }}
                                    className="p-1.5 rounded-md hover:bg-gradient-to-r hover:from-emerald-500 hover:to-green-500 text-slate-400 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                                    title="Ask more about this data"
                                >
                                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>auto_awesome</span>
                                </button>
                            )}
                            <button
                                onClick={handleZoom}
                                className="p-1.5 rounded-md hover:bg-gradient-to-r hover:from-blue-500 hover:to-primary text-slate-400 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                                title="Expand to full screen"
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>open_in_full</span>
                            </button>
                            {/* Kebab Menu */}
                            {canManageWidget && (
                            <div className="relative z-10 kebab-menu-container">
                                <button
                                    onClick={(e) => { e.stopPropagation(); setShowKebabMenu(!showKebabMenu); }}
                                    className="p-1.5 rounded-md hover:bg-gradient-to-r hover:from-slate-500 hover:to-slate-600 text-slate-400 hover:text-white transition-all duration-300 transform hover:scale-105 shadow-sm hover:shadow-md"
                                    title="More options"
                                >
                                    <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                                    </svg>
                                </button>

                                {showKebabMenu && (
                                    <div className="absolute right-0 top-8 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 min-w-[120px]">
                                        <button
                                            onClick={handleInfo}
                                            className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2 rounded-t-lg"
                                        >
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span>Info</span>
                                        </button>
                                        <button
                                            onClick={handleEdit}
                                            className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2"
                                        >
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                            <span>Edit</span>
                                        </button>
                                        <button
                                            onClick={handleExport}
                                            disabled={isExporting}
                                            className="w-full px-4 py-2 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 flex items-center space-x-2 disabled:opacity-50"
                                        >
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                            </svg>
                                            <span>{isExporting ? 'Exporting...' : getExportLabel(widget.visualization_type || 'table')}</span>
                                        </button>
                                        <button
                                            onClick={handleDelete}
                                            className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center space-x-2 rounded-b-lg"
                                        >
                                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                            <span>Delete</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                            )}
                        </div>
                    )}
                </div>



                <div className={`mx-4 mb-4 p-3 ${isTableWidget ? '' : 'flex-grow overflow-auto'}`}>
                    {getWidgetData(widget).length > 0 ? (
                        <div className={isTableWidget ? 'w-full' : 'h-full'}>
                            <DataVisualizer
                                data={getWidgetData(widget)}
                                visualizationType={widget.visualization_type || 'table'}
                                widgetId={widget.id.toString()}
                                title={widget.title}
                            />
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <div className="bg-slate-100 dark:bg-gray-800 rounded-full p-4 mb-3">
                                <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <p className="text-sm font-medium">No data available</p>
                            <p className="text-xs text-slate-300 dark:text-slate-500 mt-1">Widget data will appear here</p>
                        </div>
                    )}
                </div>


            </div>

            {/* Info Modal */}
            {showInfoModal && ReactDOM.createPortal(
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
                    onClick={(e) => e.target === e.currentTarget && handleCloseInfoModal()}
                >
                    <div
                        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center space-x-3">
                                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-xl">
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">Widget Information</h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Details and metadata</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleCloseInfoModal}
                                    className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-all duration-200"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Content */}
                            <div className="space-y-4">
                                {/* Title */}
                                <div className="bg-gradient-to-r from-slate-50 to-blue-50 dark:from-slate-800 dark:to-blue-900/20 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Title</label>
                                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 break-words">{widget.title}</p>
                                </div>

                                {/* Query */}
                                <div className="bg-gradient-to-r from-slate-50 to-green-50 dark:from-slate-800 dark:to-green-900/20 p-4 rounded-xl border border-slate-200 dark:border-slate-700">
                                    <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">Query</label>
                                    <p className="text-sm text-slate-700 dark:text-slate-300 break-words leading-relaxed">{widget.users_query || 'No query available'}</p>
                                </div>

                                {/* Details Grid */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
                                        <label className="block text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wider mb-1">Visualization</label>
                                        <p className="text-sm font-medium text-slate-800 dark:text-slate-100 capitalize">{widget.visualization_type || 'Table'}</p>
                                    </div>

                                    <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
                                        <label className="block text-xs font-semibold text-orange-600 dark:text-orange-400 uppercase tracking-wider mb-1">Records</label>
                                        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{getWidgetData(widget).length} items</p>
                                    </div>
                                </div>

                                {/* Timestamps */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-3 rounded-lg border border-emerald-200 dark:border-emerald-800">
                                        <label className="block text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider mb-1">Created</label>
                                        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{new Date(widget.created_at).toLocaleDateString()}</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(widget.created_at).toLocaleTimeString()}</p>
                                    </div>

                                    <div className="bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20 p-3 rounded-lg border border-yellow-200 dark:border-yellow-800">
                                        <label className="block text-xs font-semibold text-yellow-600 dark:text-yellow-400 uppercase tracking-wider mb-1">Updated</label>
                                        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{new Date(widget.updated_at).toLocaleDateString()}</p>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">{new Date(widget.updated_at).toLocaleTimeString()}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
                                <button
                                    onClick={handleCloseInfoModal}
                                    className="px-6 py-2.5 text-sm font-medium bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Edit Modal */}
            {showEditModal && ReactDOM.createPortal(
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
                    onClick={(e) => e.target === e.currentTarget && handleCloseEditModal()}
                >
                    <div
                        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-6">
                            {/* Header */}
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center space-x-3">
                                    <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-3 rounded-xl">
                                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100">Edit Widget Title</h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">Update the widget title</p>
                                    </div>
                                </div>
                                <button
                                    onClick={handleCloseEditModal}
                                    className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-all duration-200"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Content */}
                            <div className="mb-6">
                                <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4 rounded-xl border border-blue-200 dark:border-blue-800">
                                    <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">Widget Title</label>
                                    <input
                                        type="text"
                                        value={editTitle}
                                        onChange={(e) => setEditTitle(e.target.value)}
                                        className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                                        placeholder="Enter widget title"
                                        disabled={isUpdating}
                                        autoFocus
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                handleUpdateTitle();
                                            }
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={handleCloseEditModal}
                                    className="px-6 py-2.5 text-sm font-medium bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-300"
                                    disabled={isUpdating}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdateTitle}
                                    className="px-6 py-2.5 text-sm font-medium bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                    disabled={isUpdating || !editTitle.trim()}
                                >
                                    {isUpdating ? 'Updating...' : 'Update Title'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Zoom Modal */}
            {isZoomed && (
                <ZoomedWidgetModal widget={widget} onClose={handleCloseZoom} />
            )}

            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}
        </>
    );
};
